import React, { useState } from "react";
import {
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Snackbar,
  Alert,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

import { useSchedule } from "../../../contexts/ScheduleContext";
import { useClub } from "../../../contexts/ClubContext";
import { Club } from "../../../types/ClubTypes";
import { Game } from "../../../types/ScheduleTypes";
import { EditGameDialog } from "./EditGameDialog";
import { convert24hTo12h } from "../../../helpers/dateTime";

export const Schedule = () => {
  const { club } = useClub();
  const { games, clubs, fetchGames, updateGame, createGame, deleteGame, error } =
    useSchedule();
  const [clubId, setClubId] = useState<number | null>(club?.id || null);
  const [selectedGame, setSelectedGame] = useState<Game | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isNewGame, setIsNewGame] = useState(false);
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({
    open: false,
    message: "",
    severity: "success",
  });

  const handleClubChange = (event: SelectChangeEvent<string>) => {
    setClubId(Number(event.target.value));
    fetchGames(Number(event.target.value));
  };

  const handleEditClick = (game: Game) => {
    setSelectedGame(game);
    setIsNewGame(false);
    setIsDialogOpen(true);
  };

  const handleNewGameClick = () => {
    setSelectedGame(null);
    setIsNewGame(true);
    setIsDialogOpen(true);
  };

  const handleCloseNotification = () => {
    setNotification((prev) => ({ ...prev, open: false }));
  };

  const handleSaveGame = async (updatedGame: Game) => {
    if (!clubId) {
      setNotification({
        open: true,
        message: "Please select a club",
        severity: "error",
      });
      return;
    }
    try {
      await updateGame(updatedGame.id, updatedGame, clubId);
      handleCloseDialog();
      setNotification({
        open: true,
        message: "Game updated successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: "Failed to update game. Please try again.",
        severity: "error",
      });
      console.error("Failed to update game");
    }
  };

  const handleCreateGame = async (newGame: Game) => {
    if (!clubId) {
      setNotification({
        open: true,
        message: "Please select a club",
        severity: "error",
      });
      return;
    }
    try {
      await createGame(newGame, clubId);
      handleCloseDialog();
      setNotification({
        open: true,
        message: "Game created successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: "Failed to create game. Please try again.",
        severity: "error",
      });
      console.error("Failed to create game");
    }
  };

  const handleDeleteGame = async (gameId: number) => {
    if (!clubId) {
      setNotification({
        open: true,
        message: "Please select a club",
        severity: "error",
      });
      return;
    }
    try {
      await deleteGame(gameId, clubId);
      handleCloseDialog();
      setNotification({
        open: true,
        message: "Game deleted successfully",
        severity: "success",
      });
    } catch (err) {
      setNotification({
        open: true,
        message: "Failed to delete game. Please try again.",
        severity: "error",
      });
      console.error("Failed to delete game");
    }
  };

  const handleCloseDialog = () => {
    setSelectedGame(null);
    setIsDialogOpen(false);
    setIsNewGame(false);
  };

  const sortedGames = games?.sort((a, b) => {
    if (a.date && b.date) {
      return new Date(a.date).getTime() - new Date(b.date).getTime();
    }
    return 0;
  });

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          gap: 2,
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Club</InputLabel>
          <Select
            value={clubId?.toString()}
            label="Club"
            onChange={handleClubChange}
            sx={{
              "& .MuiSelect-icon": {
                color: "text.secondary",
              },
            }}
          >
            {clubs?.map((club: Club) => (
              <MenuItem key={club.id} value={club.id.toString()}>
                {club.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNewGameClick}
        >
          New Game
        </Button>
      </Box>
      <TableContainer component={Paper} sx={{ mt: 2 }}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Date</TableCell>
              <TableCell>Time</TableCell>
              <TableCell>Home Club</TableCell>
              <TableCell>Away Club</TableCell>
              <TableCell>Home Score</TableCell>
              <TableCell>Away Score</TableCell>
              <TableCell>Scrimmage</TableCell>
              <TableCell>Edit</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedGames?.map((game: Game) => (
              <TableRow key={game.id}>
                <TableCell>
                  {game.date}
                </TableCell>
                <TableCell>
                  {game.local_time ? convert24hTo12h(game.local_time) : ""}
                </TableCell>
                <TableCell>{game.home_club.name}</TableCell>
                <TableCell>{game.away_club.name}</TableCell>
                <TableCell>{game.home_score}</TableCell>
                <TableCell>{game.away_score}</TableCell>
                <TableCell>{game.is_scrimmage ? "Yes" : "No"}</TableCell>
                <TableCell>
                  <IconButton
                    onClick={() => handleEditClick(game)}
                    size="small"
                  >
                    <EditIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <EditGameDialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        game={isNewGame ? null : selectedGame}
        clubs={clubs || []}
        onSave={isNewGame ? handleCreateGame : handleSaveGame}
        onDelete={handleDeleteGame}
      />
      <Snackbar
        open={notification.open}
        autoHideDuration={6000}
        onClose={handleCloseNotification}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseNotification}
          severity={notification.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};
